import { useQuery } from '@tanstack/vue-query'
import { computed } from 'vue'
import { useRoute } from 'vue-router'
import { policyService } from 'src/services/api/policy.service'
import { useAccountStore } from 'src/stores/account'
import { useCompaniesDirectory } from 'src/composables/account/query/useCompaniesDirectory'
import { ROUTES } from 'src/router/const'
import { CACHE_TIMES, QUERY_KEYS, STALE_TIMES } from 'src/services/consts/queries.const'
import { POLICY_TYPES } from 'src/services/consts/policy.const'
import {
  buildPolicyIndex,
  enrichPolicyEntries,
  flattenPolicyRows,
} from 'src/services/helpers/policy.helper'
import { refetchEnabledWhenVisible } from 'src/composables/app/system/useOnlineStatus'

export function usePoliciesQuery() {
  const accountStore = useAccountStore()
  const route = useRoute()
  const directory = useCompaniesDirectory()

  const showAllowList = computed(() => !!accountStore.accountData?.settings?.showAllowList)
  const showBlockList = computed(() => !!accountStore.accountData?.settings?.showBlockList)

  const enableQuery = computed(() => {
    const hasVisibleList = showAllowList.value || showBlockList.value
    const isSupportedSurface = route.name === ROUTES.POLICIES || route.name === ROUTES.TICKET_EDIT
    return !!accountStore.accountId && hasVisibleList && isSupportedSurface
  })

  const query = useQuery({
    queryKey: [QUERY_KEYS.POLICIES],
    enabled: enableQuery,
    refetchOnWindowFocus: enableQuery,
    refetchOnReconnect: refetchEnabledWhenVisible(enableQuery),
    staleTime: STALE_TIMES.xl,
    gcTime: CACHE_TIMES.xl,
    queryFn: policyService.loadPolicies,
  })

  const hasPolicies = computed(() => query.data.value != null)
  const isPoliciesLoading = computed(() => query.isLoading.value || accountStore.isLoading)
  const policiesError = computed(() => {
    if (accountStore.error) return accountStore.error
    // Cached lists still render, so only a policies failure with nothing to show blocks the page.
    if (query.error.value && !hasPolicies.value) return query.error.value
    return null
  })

  function refetchPolicies() {
    if (accountStore.error) {
      accountStore.refetch()
      return
    }
    query.refetch()
  }

  // O(1) membership lookup for badges / ticket search — Sets of company IDs and "companyId:productId".
  const policyIndex = computed(() => buildPolicyIndex(query.data.value))
  // Flattened, directory-enriched rows for the Policy page table (filter / sort / paginate).
  const policyRows = computed(() => {
    const lists = query.data.value
    return {
      [POLICY_TYPES.ALLOW_LIST]: flattenPolicyRows(
        enrichPolicyEntries({ list: lists?.[POLICY_TYPES.ALLOW_LIST], directory })
      ),
      [POLICY_TYPES.BLOCK_LIST]: flattenPolicyRows(
        enrichPolicyEntries({ list: lists?.[POLICY_TYPES.BLOCK_LIST], directory })
      ),
    }
  })

  return {
    policies: query.data,
    policyIndex,
    policyRows,
    showAllowList,
    showBlockList,
    isPoliciesLoading,
    isPoliciesRefetching: query.isRefetching,
    policiesError,
    refetchPolicies,
  }
}
